import { onMounted, watchEffect, Ref, reactive, ref } from 'vue';
import { getLocations, LabelOverrides, Facet, Config, FacetGroup, useCategories } from '@bc/discovery/domain/catalog';

type UseLabels = (config: Ref<Config>, selectedCategoryId: Ref<number>, lang: Ref<string>) => { labelOverrides: LabelOverrides };

export const useLabels: UseLabels = (config) => {
  const { addLabelOverrides, labelOverrides } = useLabelOverrides();
  const { categoryLabelOverrides } = useCategoryLabelOverrides();

  watchEffect(() => addLabelOverrides(categoryLabelOverrides.value));

  watchEffect(async () => {
    const createLabel = (facet: Facet) => (choice: { slug: string; name: string }) => [
      `${facet.uid}:${choice.slug}`,
      choice.name,
    ];
    const mapFacetToLabelOverrides = (facet: Facet) => ('choices' in facet ? facet.choices?.map(createLabel(facet)) : []);
    const mapFacetGroupToLabelOverrides = (group: FacetGroup) => group.facets.flatMap((facet) => mapFacetToLabelOverrides(facet));

    const facetLabelOverrides = Object.fromEntries(
      config.value.Facets.flatMap((facet) =>
        'facets' in facet ? mapFacetGroupToLabelOverrides(facet) : mapFacetToLabelOverrides(facet)
      )
    );

    addLabelOverrides(facetLabelOverrides);
  });

  onMounted(async () => {
    const locations = await getLocations();

    const locationLabelOverrides = Object.fromEntries((locations ?? []).map((location) => [location.id, location.name]));
    addLabelOverrides(locationLabelOverrides);
  });

  return { labelOverrides };
};

const useLabelOverrides = () => {
  const labelOverrides = reactive<LabelOverrides>({});

  const addLabelOverrides = (overrides: Record<string, string>, keyPrefix?: string) => {
    Object.entries(overrides).forEach(([key, value]) => {
      if (keyPrefix) {
        labelOverrides[`${keyPrefix}:${key}`] = value;
      } else {
        labelOverrides[key] = value;
      }
    });
  };

  return { labelOverrides, addLabelOverrides };
};

const useCategoryLabelOverrides = () => {
  const categoryLabelOverrides = ref<LabelOverrides>({});
  const { categories } = useCategories();

  watchEffect(() => {
    categoryLabelOverrides.value = Object.fromEntries(
      Object.values(categories.value ?? {}).map((category) => [category.id, category.full_name])
    );
  });

  return { categoryLabelOverrides };
};

export const getLabel = (labelOverrides: LabelOverrides, facet: Pick<Facet, 'uid'>, originalLabel: string) => {
  return labelOverrides?.[`${facet?.uid}:${originalLabel}`] ?? labelOverrides?.[originalLabel] ?? originalLabel;
};
